import { useEffect, useState } from "react";
import { Select, DatePicker } from "antd";
import { ArrowLeftRight, Check, X, History, Undo2 } from "lucide-react";
import dayjs from "dayjs";
import {
    BRANCH_TRANSFER, BRANCH, EMPLOYEE_PERSONAL_INFO,
    EMPLOYEE_DEPARTMENT, EMPLOYEE_DESIGNATION, EMPLOYEE_TYPE, EMPLOYEE_SHIFT,
} from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { DialogModal, ConfirmPopupModal } from "../../components/ui/dialogModal";
import { Reactable } from "../../components/ui/reactable";
import { cn } from "../../utils/tailwindUtils";

const { Option } = Select;

// Branch transfer — inside the Employee tab.
//  * Request: a source-branch manager sends one of their employees to another
//    branch. Source = the employee's current branch; the DESTINATION branch's
//    manager approves (accepts them in). Temporary -> To Date required;
//    Permanent -> optional.
//  * Inbox: requests awaiting my approval (I manage the DESTINATION branch).
const BranchTransfer = () => {
    const { fetch, create, update } = useApiMethods();

    const [tab, setTab] = useState("requests"); // requests | inbox
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [trigger, setTrigger] = useState(false);
    const [openForm, setOpenForm] = useState(false);
    const [confirm, setConfirm] = useState({ open: false, row: null, action: "" });
    // Employee chosen at APPROVAL time (a request may have been raised without a
    // specific employee; the approver must pick one before approving).
    const [approveEmployee, setApproveEmployee] = useState(undefined);
    // Employees of the SOURCE branch (the branch we transfer OUT of) — the only
    // ones eligible to be transferred. Loaded when the approve dialog opens.
    const [sourceEmployees, setSourceEmployees] = useState([]);
    const [sourceEmpLoading, setSourceEmpLoading] = useState(false);
    // Approval history (audit log) for a single request.
    const [history, setHistory] = useState({ open: false, row: null });
    const [historyRows, setHistoryRows] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // dropdown data
    const [branches, setBranches] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [types, setTypes] = useState([]);
    const [shifts, setShifts] = useState([]);

    // Current branch context. A branch admin requests only FROM their own branch,
    // so Source Branch defaults to it and is locked. Org admin has no single home
    // branch (falls back to the active/selected branch) and may change it.
    const isOrgAdmin = (sessionStorage.getItem("is_admin") === "true" || sessionStorage.getItem("is_admin") === "1");
    const myBranchId = sessionStorage.getItem("branch_id");
    const activeBranchId = sessionStorage.getItem("active_branch_id");
    const defaultSourceBranch = (() => {
        const raw = myBranchId || (activeBranchId && activeBranchId !== "all" ? activeBranchId : null);
        const n = raw != null ? Number(raw) : NaN;
        return Number.isFinite(n) && n > 0 ? n : undefined;
    })();
    // Lock the source branch for a branch admin (single home branch); an org
    // admin can still pick any source.
    const lockSourceBranch = !isOrgAdmin && !!defaultSourceBranch;

    const emptyForm = {
        employee: undefined, from_branch: defaultSourceBranch, to_branch: undefined,
        transfer_type: "temporary", from_date: null, to_date: null,
        department: undefined, designation: undefined, employee_type: undefined,
        shift: undefined, comments: "",
    };
    const [form, setForm] = useState(emptyForm);

    // ---- load list (requests I raised, or my inbox) ----
    useEffect(() => {
        setLoading(true);
        const params = tab === "inbox" ? { inbox: "True" } : {};
        fetch(BRANCH_TRANSFER, params)
            .then((res) => {
                const list = res?.data?.result || res?.data || [];
                setRows(Array.isArray(list) ? list : []);
            })
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [tab, trigger]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- load dropdowns for the form ----
    useEffect(() => {
        const list = (res) => res?.data?.result || res?.data || [];
        fetch(BRANCH, { drop_down: "True" }).then((r) => setBranches(list(r))).catch(() => {});
        fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: "True" }).then((r) => setEmployees(list(r))).catch(() => {});
        fetch(EMPLOYEE_DEPARTMENT, { drop_down: "True" }).then((r) => setDepartments(list(r))).catch(() => {});
        fetch(EMPLOYEE_DESIGNATION, { drop_down: "True" }).then((r) => setDesignations(list(r))).catch(() => {});
        fetch(EMPLOYEE_TYPE, { drop_down: "True" }).then((r) => setTypes(list(r))).catch(() => {});
        fetch(EMPLOYEE_SHIFT, { drop_down: "True" }).then((r) => setShifts(list(r))).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // When the approve dialog opens for a request that has no employee yet, load
    // the SOURCE branch's employees — the people being moved OUT to the
    // destination. This is the standard "move employee from A to B" flow.
    useEffect(() => {
        if (!(confirm.open && confirm.action === "approved")) return;
        if (confirm.row?.employee_id) return;                 // already has one
        const branchId = confirm.row?.from_branch_id;
        if (!branchId) { setSourceEmployees([]); return; }
        setSourceEmpLoading(true);
        // Dedicated lightweight endpoint on the branch-transfer view (avoids the
        // heavy employee/info endpoint that was timing out).
        fetch(BRANCH_TRANSFER, { employees_for_branch: branchId })
            .then((r) => {
                const list = r?.data?.result || r?.data || [];
                setSourceEmployees(Array.isArray(list) ? list : []);
            })
            .catch(() => setSourceEmployees([]))
            .finally(() => setSourceEmpLoading(false));
    }, [confirm.open, confirm.action, confirm.row]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load the approval history when the History dialog opens.
    useEffect(() => {
        if (!(history.open && history.row?.id)) return;
        setHistoryLoading(true);
        setHistoryRows([]);
        fetch(BRANCH_TRANSFER, { history: history.row.id })
            .then((r) => {
                const list = r?.data?.result || r?.data || [];
                setHistoryRows(Array.isArray(list) ? list : []);
            })
            .catch(() => setHistoryRows([]))
            .finally(() => setHistoryLoading(false));
    }, [history.open, history.row]); // eslint-disable-line react-hooks/exhaustive-deps

    const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async () => {
        if (!form.from_branch || !form.to_branch) {
            ToastMsg("error", "Source and destination branch are required");
            return;
        }
        if (!form.from_date) {
            ToastMsg("error", "From Date is required");
            return;
        }
        if (form.transfer_type === "temporary" && !form.to_date) {
            ToastMsg("error", "To Date is required for a temporary transfer");
            return;
        }
        try {
            setLoading(true);
            const payload = {
                ...form,
                from_date: form.from_date ? dayjs(form.from_date).format("YYYY-MM-DD") : null,
                to_date: form.to_date ? dayjs(form.to_date).format("YYYY-MM-DD") : null,
            };
            const res = await create(BRANCH_TRANSFER, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Transfer request submitted");
                setForm(emptyForm);
                setOpenForm(false);
                setTrigger((p) => !p);
            } else {
                ToastMsg("error", res?.message || "Failed to submit request");
            }
        } catch (e) {
            /* interceptor toasts */
        } finally {
            setLoading(false);
        }
    };

    const act = async () => {
        const { row, action } = confirm;
        // Approving requires an employee: use the one already on the request, or
        // the one the approver just selected. Reject needs no employee.
        const employeeId = row?.employee_id || approveEmployee;
        if (action === "approved" && !employeeId) {
            ToastMsg("error", "Select the employee to transfer before approving");
            return;
        }
        try {
            setLoading(true);
            const payload = { id: row.id, approval_status: action };
            if (action === "approved") payload.employee = employeeId;
            const res = await update(BRANCH_TRANSFER, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message);
                setTrigger((p) => !p);
            } else {
                ToastMsg("error", res?.message || "Failed");
            }
        } catch (e) {
            /* interceptor toasts */
        } finally {
            setLoading(false);
            setConfirm({ open: false, row: null, action: "" });
            setApproveEmployee(undefined);
        }
    };

    const statusPill = (s) => {
        const map = {
            pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
            approved: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
            rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
            returned: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        };
        return <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium", map[s] || map.returned)}>{s}</span>;
    };

    const field = "w-full";
    const label = "text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block";

    // Columns for the hrbuddie-themed Reactable. Rebuilt on tab change so the
    // Action column only appears in the Approval Inbox.
    const columns = [
        {
            accessor: "employee",
            Header: "Employee",
            Cell: ({ row }) => {
                const r = row.original || row;
                const name = `${r.employee__first_name || ""} ${r.employee__last_name || ""}`.trim();
                return (
                    <div>
                        <div>{name || "-"}</div>
                        {r.employee__employee_id && <div className="text-xs text-gray-400">{r.employee__employee_id}</div>}
                    </div>
                );
            },
        },
        {
            accessor: "route",
            Header: "From → To",
            Cell: ({ row }) => {
                const r = row.original || row;
                return <span>{r.from_branch__name || "-"} &rarr; {r.to_branch__name || "-"}</span>;
            },
        },
        {
            accessor: "transfer_type",
            Header: "Type",
            Cell: ({ row }) => <span className="capitalize">{(row.original || row).transfer_type}</span>,
        },
        {
            accessor: "dates",
            Header: "Dates",
            Cell: ({ row }) => {
                const r = row.original || row;
                return <span>{r.from_date || "-"}{r.to_date ? ` → ${r.to_date}` : ""}</span>;
            },
        },
        {
            accessor: "approval_status",
            Header: "Status",
            Cell: ({ row }) => statusPill((row.original || row).approval_status),
        },
        {
            accessor: "history",
            Header: "History",
            align: "center",
            Cell: ({ row }) => {
                const r = row.original || row;
                // Revert is offered only for an approved PERMANENT transfer that
                // hasn't already been reverted — it sends the employee back to
                // their original branch. Temporary transfers auto-return.
                const canRevert = r.transfer_type === "permanent"
                    && r.approval_status === "approved" && !r.revert_status;
                return (
                    <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="ghost" title="History"
                            className="text-sidebar-primary hover:bg-gray-100 dark:hover:bg-gray-700"
                            onClick={() => setHistory({ open: true, row: r })}>
                            <History className="h-4 w-4" />
                        </Button>
                        {canRevert && (
                            <Button size="sm" variant="ghost" title="Revert to original branch"
                                className="text-amber-600 hover:bg-amber-50"
                                onClick={() => setConfirm({ open: true, row: r, action: "reverted" })}>
                                <Undo2 className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                );
            },
        },
        ...(tab === "inbox" ? [{
            accessor: "action",
            Header: "Action",
            align: "center",
            Cell: ({ row }) => {
                const r = row.original || row;
                return r.approval_status === "pending" ? (
                    <div className="flex gap-2 justify-center">
                        <Button size="sm" variant="ghost" className="text-green-600 hover:bg-green-50"
                            onClick={() => setConfirm({ open: true, row: r, action: "approved" })}>
                            <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50"
                            onClick={() => setConfirm({ open: true, row: r, action: "rejected" })}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                ) : <span className="text-xs text-gray-400">—</span>;
            },
        }] : []),
    ];

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={ArrowLeftRight}
                title={"Branch Transfer"}
                subtitle="Send an employee to another branch; the destination branch's manager approves."
                buttonEnable={true}
                buttonName="New Request"
                setOpenDialog={() => setOpenForm(true)}
            />

            {/* tabs */}
            <div className="flex gap-2 mb-4">
                {[["requests", "My Requests"], ["inbox", "Approval Inbox"]].map(([k, lbl]) => (
                    <button
                        key={k}
                        onClick={() => setTab(k)}
                        className={cn(
                            "px-4 py-1.5 rounded-lg text-sm font-medium transition-colors",
                            tab === k ? "bg-sidebar-primary text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                        )}
                    >
                        {lbl}
                    </button>
                ))}
            </div>

            {/* list — hrbuddie-themed Reactable (client-side; rows fetched per tab) */}
            <Reactable
                key={tab}
                data={rows}
                setData={setRows}
                columns={columns}
                tableHeight="calc(100vh - 340px)"
                isCSVRequired
                isPDFRequired
            />
            {loading && (
                <div className="py-8 text-center text-gray-400 text-sm">Loading…</div>
            )}

            {/* request form */}
            <DialogModal
                open={openForm}
                onClose={() => { setForm(emptyForm); setOpenForm(false); }}
                onSave={submit}
                title="New Branch Transfer Request"
                saveLabel="Submit"
                loading={loading}
                width="560px"
            >
                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <label className={label}>Employee</label>
                        <Select showSearch optionFilterProp="children" className={field} size="large"
                            placeholder="Select employee" value={form.employee}
                            onChange={(v) => {
                                // Picking an employee snaps Source Branch to that
                                // employee's CURRENT branch, so the transfer direction
                                // is always correct (source = where they are now).
                                const picked = employees.find((e) => e.id === v);
                                const raw = picked?.temp_branch_id
                                    || (Array.isArray(picked?.branch_id) ? picked.branch_id?.[0]?.id : picked?.branch_id);
                                const empBranch = Number.isFinite(Number(raw)) && Number(raw) > 0 ? Number(raw) : undefined;
                                setForm((p) => ({ ...p, employee: v, from_branch: empBranch ?? p.from_branch }));
                            }}>
                            {employees.map((e) => (
                                <Option key={e.id} value={e.id}>
                                    {`${e.first_name || ""} ${e.last_name || ""}`.trim() || e.employee_id} ({e.employee_id})
                                </Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <label className={label}>Source Branch (from) <span className="text-red-500">*</span></label>
                        {/* Read-only, always derived: the employee's current branch
                            (once picked), else the manager's own branch. Never a free
                            picker, so the transfer direction can't be wrong. */}
                        <div className="w-full h-10 px-3 flex items-center rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                            {(() => {
                                const b = branches.find((x) => Number(x.id) === Number(form.from_branch));
                                return b ? b.name : (form.employee ? "—" : "Select an employee first");
                            })()}
                        </div>
                        {!isOrgAdmin && (
                            <p className="mt-1 text-xs text-gray-400">You can send only from your branch.</p>
                        )}
                    </div>
                    <div>
                        <label className={label}>Destination Branch (to) <span className="text-red-500">*</span></label>
                        <Select className={field} size="large" placeholder="Branch to send to" value={form.to_branch}
                            onChange={(v) => setF("to_branch", v)}>
                            {branches.filter((b) => Number(b.id) !== Number(form.from_branch))
                                .map((b) => <Option key={b.id} value={b.id}>{b.name}</Option>)}
                        </Select>
                    </div>
                    <div>
                        <label className={label}>Transfer Type</label>
                        <Select className={field} size="large" value={form.transfer_type}
                            onChange={(v) => setF("transfer_type", v)}>
                            <Option value="temporary">Temporary</Option>
                            <Option value="permanent">Permanent</Option>
                        </Select>
                    </div>
                    <div>
                        <label className={label}>Department</label>
                        <Select allowClear className={field} size="large" placeholder="Any" value={form.department}
                            onChange={(v) => setF("department", v)}>
                            {departments.map((d) => <Option key={d.id} value={d.id}>{d.department_name}</Option>)}
                        </Select>
                    </div>
                    <div>
                        <label className={label}>Designation</label>
                        <Select allowClear className={field} size="large" placeholder="Any" value={form.designation}
                            onChange={(v) => setF("designation", v)}>
                            {designations.map((d) => <Option key={d.id} value={d.id}>{d.designation_name}</Option>)}
                        </Select>
                    </div>
                    <div>
                        <label className={label}>Employee Type</label>
                        <Select allowClear className={field} size="large" placeholder="Any" value={form.employee_type}
                            onChange={(v) => setF("employee_type", v)}>
                            {types.map((t) => <Option key={t.id} value={t.id}>{t.employee_type_name}</Option>)}
                        </Select>
                    </div>
                    <div>
                        <label className={label}>Shift</label>
                        <Select allowClear className={field} size="large" placeholder="Any" value={form.shift}
                            onChange={(v) => setF("shift", v)}>
                            {shifts.map((s) => <Option key={s.id} value={s.id}>{s.shift_name}</Option>)}
                        </Select>
                    </div>
                    <div>
                        <label className={label}>From Date <span className="text-red-500">*</span></label>
                        <DatePicker className={field} size="large" value={form.from_date}
                            onChange={(d) => setF("from_date", d)} />
                    </div>
                    <div>
                        <label className={label}>
                            To Date {form.transfer_type === "temporary" && <span className="text-red-500">*</span>}
                        </label>
                        <DatePicker className={field} size="large" value={form.to_date}
                            onChange={(d) => setF("to_date", d)} />
                    </div>
                </div>
            </DialogModal>

            {/* Approve: needs an employee selection (the request may not name one).
                Uses DialogModal so we can embed the employee Select. */}
            <DialogModal
                open={confirm.open && confirm.action === "approved"}
                onClose={() => { setConfirm({ open: false, row: null, action: "" }); setApproveEmployee(undefined); }}
                onSave={act}
                title="Approve Transfer"
                saveLabel="Approve"
                loading={loading}
                width="460px"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Approving transfer&nbsp;
                        <span className="font-medium">{confirm.row?.from_branch__name || "-"}</span> &rarr;{" "}
                        <span className="font-medium">{confirm.row?.to_branch__name || "-"}</span>
                        {confirm.row?.transfer_type ? ` (${confirm.row.transfer_type})` : ""}.
                    </p>
                    <div>
                        <label className={label}>Employee <span className="text-red-500">*</span></label>
                        {confirm.row?.employee_id ? (
                            <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200">
                                {`${confirm.row.employee__first_name || ""} ${confirm.row.employee__last_name || ""}`.trim() || "-"}
                                {confirm.row.employee__employee_id ? ` (${confirm.row.employee__employee_id})` : ""}
                            </div>
                        ) : (
                            <>
                                <Select showSearch optionFilterProp="children" className={field} size="large"
                                    loading={sourceEmpLoading}
                                    placeholder={sourceEmpLoading ? "Loading employees…" : `Select an employee from ${confirm.row?.from_branch__name || "the source branch"}`}
                                    notFoundContent={sourceEmpLoading ? "Loading…" : "No employees in this branch"}
                                    value={approveEmployee} onChange={setApproveEmployee}>
                                    {sourceEmployees.map((e) => (
                                        <Option key={e.id} value={e.id}>
                                            {`${e.first_name || ""} ${e.last_name || ""}`.trim() || e.employee_id} ({e.employee_id})
                                        </Option>
                                    ))}
                                </Select>
                                <p className="mt-1 text-xs text-gray-400">
                                    Only employees of {confirm.row?.from_branch__name || "the source branch"} can be selected.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </DialogModal>

            {/* Reject: simple confirm, no employee needed. */}
            <ConfirmPopupModal
                open={confirm.open && confirm.action === "rejected"}
                onClose={() => setConfirm({ open: false, row: null, action: "" })}
                onConfirm={act}
                title="Reject Transfer"
                type="danger"
                destructive={true}
                description={`Reject the transfer of ${confirm.row ? (`${confirm.row.employee__first_name || ""} ${confirm.row.employee__last_name || ""}`.trim() || "this request") : "this request"}?`}
                confirmLabel="Reject"
                loading={loading}
            />

            {/* Revert: send a permanently-transferred employee back to their original branch. */}
            <ConfirmPopupModal
                open={confirm.open && confirm.action === "reverted"}
                onClose={() => setConfirm({ open: false, row: null, action: "" })}
                onConfirm={act}
                title="Revert Transfer"
                type="warning"
                description={confirm.row
                    ? `Send ${(`${confirm.row.employee__first_name || ""} ${confirm.row.employee__last_name || ""}`.trim() || "this employee")} back to ${confirm.row.from_branch__name || "their original branch"}? This undoes the permanent transfer.`
                    : "Undo this permanent transfer?"}
                confirmLabel="Revert"
                loading={loading}
            />

            {/* Approval history (audit log) */}
            <DialogModal
                open={history.open}
                onClose={() => { setHistory({ open: false, row: null }); setHistoryRows([]); }}
                title="Transfer History"
                showActions={false}
                width="520px"
            >
                {history.row && (
                    <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                        {history.row.from_branch__name || "-"} &rarr; {history.row.to_branch__name || "-"}
                        {history.row.transfer_type ? ` (${history.row.transfer_type})` : ""}
                    </p>
                )}
                {historyLoading && <p className="py-6 text-center text-gray-400">Loading…</p>}
                {!historyLoading && historyRows.length === 0 && (
                    <p className="py-6 text-center text-gray-400">No history yet.</p>
                )}
                {!historyLoading && historyRows.length > 0 && (
                    <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-2">
                        {historyRows.map((h) => {
                            const actor = `${h.approved_by__first_name || ""} ${h.approved_by__last_name || ""}`.trim();
                            const when = h.approved_date_and_time
                                ? dayjs(h.approved_date_and_time).format("DD MMM YYYY, hh:mm A")
                                : "";
                            return (
                                <li key={h.id} className="mb-4 ml-4">
                                    <span className="absolute -left-1.5 flex h-3 w-3 rounded-full bg-sidebar-primary" />
                                    <div className="flex items-center gap-2">
                                        {statusPill(h.approval_status)}
                                        <span className="text-xs text-gray-400">{when}</span>
                                    </div>
                                    <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                                        {actor ? `by ${actor}` : ""}
                                        {h.approved_by__employee_id ? ` (${h.approved_by__employee_id})` : ""}
                                    </div>
                                    {h.approval_notes && (
                                        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 italic">
                                            “{h.approval_notes}”
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </DialogModal>
        </ContainerCard>
    );
};

export default BranchTransfer;
